import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import { FaPrint, FaFilePdf, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import { getAttendance, getMonthlyAttendance } from '../../../services/firebase/attendanceService';
import { getCalendarDays, mapAttendanceToCalendar, getCurrentMonthYear, normalizeStatus } from '../../../utils/calendarUtils';
import EmployeeSelector from './EmployeeSelector';
import MonthNavigator from './MonthNavigator';
import CalendarGrid from './CalendarGrid';
import CalendarLegend from './CalendarLegend';
import AttendanceDetailModal from './AttendanceDetailModal';
import AttendanceSummaryCard from './AttendanceSummaryCard';
import AttendanceGraph from './AttendanceGraph'; // Added Graph
import EditAttendanceModal from '../EditAttendanceModal';
import AddFestivalModal from './AddFestivalModal';
import { getFestivals, deleteFestival } from '../../../services/firebase/festivalService';
import { triggerFestivalCelebration } from '../../../utils/celebrationConfetti';
import moment from 'moment';
import './Calendar.css';

const AttendanceCalendarView = ({ 
  initialEmployeeId = '', 
  hideEmployeeSelector = false, 
  hideMonthNavigator = false,
  hideStats = false,
  isDashboard = false,
  showDashboardStats = false // New prop for dashboard summary tiles
}) => {
  const { user } = useAuth();
  const userRole = (user?.role || '').toLowerCase().trim();
  const userDesignation = (user?.designation || user?.job?.designation || '').toLowerCase().trim();
  const isEmployee = userRole === 'employee';
  const isPrivilegedUser = ['admin', 'hr', 'manager', 'ceo', 'founder'].includes(userRole) || userDesignation.includes('founder');
  // Restricted to admin, hr, ceo, founder:
  const canManageFestivals = ['admin', 'hr', 'ceo', 'founder', 'superadmin'].includes(userRole) || 
    userDesignation === 'ceo' || 
    userDesignation === 'founder' ||
    userDesignation.includes('founder') ||
    userDesignation.includes('ceo');
  const FestivalModal = (AddFestivalModal && AddFestivalModal.default) || AddFestivalModal;
  const DetailModal = (AttendanceDetailModal && AttendanceDetailModal.default) || AttendanceDetailModal;

  const currentMonthYear = getCurrentMonthYear();
  
  // Use prop if provided, otherwise default logic
  const [selectedEmployee, setSelectedEmployee] = useState(
    initialEmployeeId || (isEmployee ? (user.employeeCode || user.employeeId || user.uid) : '')
  );
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState(
    initialEmployeeId || (isEmployee ? (user.employeeCode || user.employeeId || user.uid) : '')
  );
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
  const [month, setMonth] = useState(currentMonthYear.month);
  const [year, setYear] = useState(currentMonthYear.year);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [showFestivalModal, setShowFestivalModal] = useState(false);
  const [festivalModalDate, setFestivalModalDate] = useState('');

  const loadFestivals = async () => {
    try {
      const list = await getFestivals();
      const validList = Array.isArray(list) ? list : [];
      setFestivals(validList);
      if (!selectedEmployee) {
        const days = getCalendarDays(month, year);
        setCalendarData(mapAttendanceToCalendar(days, [], validList));
      }
      return validList;
    } catch (e) {
      console.warn('Failed to load festivals:', e);
      return [];
    }
  };

  useEffect(() => {
    loadFestivals();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      fetchAttendance();
    } else {
      // Display festivals on calendar even when no employee is selected!
      const days = getCalendarDays(month, year);
      setCalendarData(mapAttendanceToCalendar(days, [], festivals));
    }
  }, [selectedEmployee, selectedEmployeeCode, selectedEmployeeName, month, year, festivals]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      // Use getMonthlyAttendance to support both UID, Employee Code and Name matching
      // This ensures data from Excel uploads (linked by Code) and manual entries (linked by UID) both appear
      const empIdToUse = selectedEmployeeCode || selectedEmployee;

      const data = await getMonthlyAttendance(month, year, empIdToUse, selectedEmployeeName);

      // Fetch festivals concurrently
      const currentFestivals = await loadFestivals();

      // Firebase service returns {success: true, data: [...]}
      const records = data?.data || data || [];
      setAttendanceRecords(Array.isArray(records) ? records : []);

      // Map to calendar with festivals
      const days = getCalendarDays(month, year);
      const mappedData = mapAttendanceToCalendar(days, records, currentFestivals);
      setCalendarData(mappedData);

      // Calculate summary
      calculateSummary(records, currentFestivals);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setAttendanceRecords([]);
      const days = getCalendarDays(month, year);
      setCalendarData(mapAttendanceToCalendar(days, [], festivals));
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (records, festivalsList = festivals) => {
    const totalDaysInMonth = new Date(year, month, 0).getDate();

    let presentDays = 0;
    let absentDays = 0;
    let leaveDays = 0;
    let halfDays = 0;
    let weeklyOffs = 0;
    let holidays = 0;
    let lateMarks = 0;
    let morningMissPunch = 0;
    let eveningMissPunch = 0;
    let earlyGoing = 0;
    let extraWorkDays = 0;

    // Detailed logs for PDF
    const lateMarkLogs = [];
    const missPunchLogs = [];
    const earlyGoingLogs = [];

    // Create a map of existing records for easy lookup
    const recordsMap = {};

    records.forEach(r => {
      let dateKey;
      if (r.date && r.date._seconds) {
        dateKey = moment.unix(r.date._seconds).format('YYYY-MM-DD');
      } else if (r.date && r.date.seconds) {
        dateKey = moment.unix(r.date.seconds).format('YYYY-MM-DD');
      } else if (r.date && (r.date instanceof Date || (typeof r.date === 'string' && r.date.includes('T')))) {
        dateKey = moment(r.date).format('YYYY-MM-DD');
      } else if (r.dateString) {
        dateKey = r.dateString;
      } else if (r.date) {
        dateKey = moment(r.date).format('YYYY-MM-DD');
      }

      if (dateKey) {
        if (recordsMap[dateKey]) {
          const existing = recordsMap[dateKey];
          const isRecordExtra = r.status === 'Extra Work' || r.isExtraWork;
          const isExistingExtra = existing.status === 'Extra Work' || existing.isExtraWork;
          const hasPunch = (v) => v && v !== '-' && v !== '' && /[1-9]/.test(String(v));

          if (isRecordExtra && !isExistingExtra) {
            recordsMap[dateKey] = { ...existing, extraWork: r, hasExtraWork: true };
          } else if (!isRecordExtra && isExistingExtra) {
            recordsMap[dateKey] = { ...r, extraWork: existing, hasExtraWork: true };
          } else {
            const recordPunchCount = (hasPunch(r.inTime) ? 1 : 0) + (hasPunch(r.outTime) ? 1 : 0);
            const existingPunchCount = (hasPunch(existing.inTime) ? 1 : 0) + (hasPunch(existing.outTime) ? 1 : 0);
            if (recordPunchCount >= existingPunchCount) {
              recordsMap[dateKey] = r;
            }
          }
        } else {
          recordsMap[dateKey] = r;
        }
      }
    });

    const dailyReport = [];
    let totalMonthMinutes = 0;
    let totalMonthOvertimeMinutes = 0;

    // Iterate through every day of the month
    for (let d = 1; d <= totalDaysInMonth; d++) {
      const dateString = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const date = new Date(year, month - 1, d);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 4 = Thursday
      const formattedDate = moment(date).format('DD MMM');

      const isFestival = (festivalsList || []).some(f => moment(f.date).format('YYYY-MM-DD') === dateString);

      const record = recordsMap[dateString];
      let dailyMinutes = 0;

      const dayResult = {
        date: formattedDate,
        day: moment(date).format('ddd'),
        in: '-',
        out: '-',
        hours: '-',
        status: (isFestival && dayOfWeek !== 2) ? 'Festival' : (dayOfWeek === 2 ? 'W.Off' : 'Absent')
      };

      const today = moment().startOf('day');
      const isPastOrToday = moment(date).isSameOrBefore(today, 'day');

      if (record) {
        // 1. Flexible punch detection (detects time values with digits)
        const hasPunch = (v) => v && v !== '-' && v !== '' && /[1-9]/.test(String(v));
        const hasIn = hasPunch(record.inTime);
        const hasOut = hasPunch(record.outTime);

        // 2. Initial status normalization
        let normalizedStatus = normalizeStatus(record.status);

        // 3. Logic Overrides
        if (normalizedStatus === 'Leave' || normalizedStatus === 'Half-day' || normalizedStatus === 'Holiday' || normalizedStatus === 'Extra Work') {
          // Keep explicit statuses
        }
        // Festival Rule (Except Tuesday): If festival day and no punches or status is Absent -> Festival (paid leave, counted as Present)
        else if (isFestival && dayOfWeek !== 2 && ((!hasIn && !hasOut) || normalizedStatus === 'Absent')) {
          normalizedStatus = 'Festival';
        }
        // 4. Weekly Off Rule (MODIFIED): Default to W.Off on Tuesday ONLY if no punches
        else if (dayOfWeek === 2 && !hasIn && !hasOut) {
          normalizedStatus = 'W.Off';
        }
        else if ((hasIn && !hasOut) || (!hasIn && hasOut)) {
          normalizedStatus = 'Miss-Punch';
        } else if (!hasIn && !hasOut) {
          normalizedStatus = (isFestival && dayOfWeek !== 2) ? 'Festival' : (dayOfWeek === 2 ? 'W.Off' : 'Absent');
        } else {
          normalizedStatus = 'Present';
        }

        // 4. Detailed Calculations (Hours & Duration-based Half-day / Early Going)
        if (hasIn && hasOut) {
          const inTime = moment(record.inTime, ["HH:mm", "h:mm A", "H:mm"]);
          const outTime = moment(record.outTime, ["HH:mm", "h:mm A", "H:mm"]);
          if (inTime.isValid() && outTime.isValid()) {
            const dailyMinutes = outTime.diff(inTime, 'minutes');
            const outMinutes = outTime.hours() * 60 + outTime.minutes();

            if (dailyMinutes > 0) {
              totalMonthMinutes += dailyMinutes;
              dayResult.hours = `${Math.floor(dailyMinutes / 60)}h ${dailyMinutes % 60}m`;

              // Calculate Overtime (Any time after 6:30 PM / 18:30)
              // 18:30 is 1110 minutes from start of day
              const outMinutesFromMidnight = outTime.hours() * 60 + outTime.minutes();
              const otThreshold = 18 * 60 + 30; // 1110
              
              if (outMinutesFromMidnight > otThreshold) {
                const dayOvertimeMinutes = outMinutesFromMidnight - otThreshold;
                totalMonthOvertimeMinutes += dayOvertimeMinutes;
                dayResult.overtime = `${Math.floor(dayOvertimeMinutes / 60)}h ${dayOvertimeMinutes % 60}m`;
              } else {
                dayResult.overtime = '-';
              }

              // Only override Present if it's currently a working day status
              if (normalizedStatus === 'Present') {
                if (dailyMinutes < 300) {
                  normalizedStatus = 'Half-day';
                } else if (inTime.hours() * 60 + inTime.minutes() > (9 * 60 + 35)) {
                  normalizedStatus = 'Late';
                } else if (outMinutes >= (17 * 60) && outMinutes < (18 * 60 + 30)) {
                  // Early Going Detection
                  normalizedStatus = 'EarlyGoing';
                }
              }
            }
          }
        }

        // 5. Finalize status and counters
        dayResult.status = normalizedStatus;
        dayResult.in = record.inTime || '-';
        dayResult.out = record.outTime || '-';

        if (isPastOrToday || normalizedStatus === 'Extra Work' || normalizedStatus === 'Festival') {
          // Present, Late, EarlyGoing, Miss-Punch, Extra Work, and Festival (paid leave) count as valid Present days
          if (
            normalizedStatus === 'Present' ||
            normalizedStatus === 'Late' ||
            normalizedStatus === 'EarlyGoing' ||
            normalizedStatus === 'Extra Work' ||
            normalizedStatus === 'Miss-Punch' ||
            normalizedStatus === 'Festival'
          ) {
            presentDays++;
          }

          if (normalizedStatus === 'Absent') absentDays++;
          else if (normalizedStatus === 'Leave') leaveDays++;
          else if (normalizedStatus === 'Half-day') halfDays++;
          else if (normalizedStatus === 'Holiday') holidays++;
          else if (normalizedStatus === 'W.Off' || normalizedStatus === 'WeeklyOff') weeklyOffs++;
          else if (normalizedStatus === 'Extra Work') extraWorkDays++;

          // Incident logs
          if (normalizedStatus === 'Late') {
            lateMarks++;
            lateMarkLogs.push({ date: formattedDate, time: record.inTime });
          }

          if (normalizedStatus === 'EarlyGoing') {
            earlyGoing++;
            earlyGoingLogs.push({ date: formattedDate, time: record.outTime });
          }
        }

        // 6. Detailed Incident Logs (Remaining cases)
        if (normalizedStatus === 'Miss-Punch' && isPastOrToday) {
          if (!hasIn) {
            morningMissPunch++;
            missPunchLogs.push({ date: formattedDate, type: 'Morning', detail: 'Missing In' });
          } else if (!hasOut) {
            eveningMissPunch++;
            missPunchLogs.push({ date: formattedDate, type: 'Evening', detail: 'Missing Out' });
          }
        }
      } else if (isPastOrToday) {
        // No record exists in database for this day
        if (isFestival && dayOfWeek !== 2) {
          // Paid festival leave (not Tuesday) -> Count as Present!
          presentDays++;
          dayResult.status = 'Festival';
        } else if (dayOfWeek === 2) {
          weeklyOffs++;
          dayResult.status = 'W.Off';
        } else {
          absentDays++;
          dayResult.status = 'Absent';
        }
      }

      dailyReport.push(dayResult);
    }

    // Final Metric Processing
    const totalWorkingDays = totalDaysInMonth - weeklyOffs; // Simple base
    const getPercent = (count) => totalWorkingDays > 0 ? ((count / totalWorkingDays) * 100).toFixed(1) : 0;

    // Day count for Miss-Punch status (The category that replaced Absent/Present for these days)
    const missPunchDayCount = dailyReport.filter(d => d.status === 'Miss-Punch' && moment(`${year}-${month}-${d.date.split(' ')[0]}`, 'YYYY-MM-DD').isSameOrBefore(moment(), 'day')).length;

    setSummary({
      totalDays: totalDaysInMonth,
      totalWorkingDays,
      employeeName: records[0]?.employeeName || 'Unknown',
      employeeCode: records[0]?.employeeId || selectedEmployeeCode || selectedEmployee,
      presentDays,
      presentPercent: getPercent(presentDays),
      absentDays,
      absentPercent: getPercent(absentDays),
      leaveDays,
      leavePercent: getPercent(leaveDays),
      halfDays,
      halfDayPercent: getPercent(halfDays),
      weeklyOffs,
      weeklyOffPercent: getPercent(weeklyOffs),
      holidays,
      holidayPercent: getPercent(holidays),
      lateMarks,
      lateMarkPercent: getPercent(lateMarks),
      morningMissPunch,
      eveningMissPunch,
      totalMissPunch: missPunchDayCount,
      missPunchPercent: getPercent(missPunchDayCount),
      earlyGoing,
      earlyGoingPercent: getPercent(earlyGoing),
      extraWorkDays,
      lateMarkLogs,
      missPunchLogs,
      earlyGoingLogs,
      dailyReport,
      totalMonthHours: `${Math.floor(totalMonthMinutes / 60)} Hours, ${totalMonthMinutes % 60} Minutes`,
      totalMonthOvertime: `${Math.floor(totalMonthOvertimeMinutes / 60)} Hours, ${totalMonthOvertimeMinutes % 60} Minutes`,
      totalMonthMinutes,
      totalMonthOvertimeMinutes
    });
  };

  const handleMonthChange = (newMonth, newYear) => {
    setMonth(newMonth);
    setYear(newYear);
  };

  const handleEmployeeChange = (employeeId) => {
    setSelectedEmployee(employeeId);
    // If clearing selection, also clear code and name
    if (!employeeId) {
      setSelectedEmployeeCode('');
      setSelectedEmployeeName('');
    }
  };

  const handleEmployeeSelect = (employee) => {
    if (employee) {
      // Store the employee code and full name
      setSelectedEmployeeCode(employee.employeeId || employee.uid);
      setSelectedEmployeeName(employee.fullName || '');
    }
  };

  const handleDayClick = (dayData, event) => {
    if (dayData.isCurrentMonth) {
      const dateKey = moment(dayData.date).format('YYYY-MM-DD');
      const isFest = Boolean(
        dayData.festival || 
        dayData.attendance?.festival || 
        dayData.attendance?.status === 'Festival' ||
        festivals.some(f => moment(f.date).format('YYYY-MM-DD') === dateKey)
      );
      if (isFest) {
        const x = event?.clientX;
        const y = event?.clientY;
        triggerFestivalCelebration(x, y);
      }
      setSelectedDay(dayData);
      setShowDetailModal(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    if (!summary || !selectedEmployee) {
      alert('Please select an employee and wait for data to load');
      return;
    }

    try {
      setLoading(true);
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');

      const monthName = moment(`${year}-${month}`, 'YYYY-MM').format('MMMM');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = 1000;

      // Helper to capture and add section to PDF
      const addSection = async (htmlContent, chartElement = null, isFirst = false) => {
        if (!isFirst) pdf.addPage();

        const div = document.createElement('div');
        div.style.width = `${contentWidth}px`;
        div.style.padding = '60px 40px 100px 40px'; // Increased bottom padding for footer clearance
        div.style.backgroundColor = '#ffffff';
        div.style.fontFamily = "'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
        div.innerHTML = htmlContent;

        if (chartElement) {
          const target = div.querySelector('#chart-clone-target');
          if (target) {
            const clone = chartElement.cloneNode(true);
            target.appendChild(clone);
          }
        }

        document.body.appendChild(div);
        const canvas = await html2canvas(div, { 
          scale: 2, 
          useCORS: true, 
          logging: false, 
          windowWidth: contentWidth,
          scrollY: -window.scrollY 
        });
        document.body.removeChild(div);

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - 10; // Margin
        let imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Safety check to ensure it fits on one page
        if (imgHeight > pageHeight - 15) {
          imgHeight = pageHeight - 15;
          // Re-calculate width to keep aspect ratio if needed, but here we prioritize height
        }

        pdf.addImage(imgData, 'PNG', 5, 5, imgWidth, imgHeight);
      };

      // Target specific chart elements
      const analysisCardEl = document.getElementById('attendance-analysis-card');
      const ratioChartEl = document.getElementById('attendance-ratio-container');

      // SECTION 1: OVERVIEW & FULL ANALYSIS CARD
      await addSection(`
        <div style="border-bottom: 5px solid #0056b3; padding-bottom: 25px; margin-bottom: 35px; padding-top: 10px;">
          <h1 style="color: #0056b3; margin: 0; text-align: center; text-transform: uppercase; font-size: 2.2rem; letter-spacing: 2px; font-weight: 800;">Monthly Attendance Report</h1>
          <div style="display: flex; justify-content: space-between; margin-top: 30px; font-size: 1.2rem; background: #f8fbff; padding: 25px; border-radius: 15px; border: 1px solid #d1e3f8;">
            <div>
              <p style="margin: 8px 0; color: #555;"><strong>Employee Name:</strong> <span style="color: #000; margin-left: 10px;">${summary.employeeName}</span></p>
              <p style="margin: 8px 0; color: #555;"><strong>Employee ID:</strong> <span style="color: #000; margin-left: 10px;">${summary.employeeCode}</span></p>
              <p style="margin: 8px 0; color: #555;"><strong>Department:</strong> <span style="color: #000; margin-left: 10px;">Operations</span></p>
            </div>
            <div style="text-align: right;">
              <p style="margin: 8px 0; color: #555;"><strong>Period:</strong> <span style="color: #000; margin-left: 10px;">${monthName} ${year}</span></p>
              <p style="margin: 8px 0; color: #555;"><strong>Efficiency:</strong> <span style="color: #28a745; font-weight: bold; font-size: 1.4rem; margin-left: 10px;">${summary.presentPercent}%</span></p>
              <p style="margin: 8px 0; color: #555;"><strong>Total Duty Hours:</strong> <span style="color: #0056b3; font-weight: bold; margin-left: 10px;">${summary.totalMonthHours}</span></p>
              <p style="margin: 8px 0; color: #555;"><strong>Total Overtime:</strong> <span style="color: #f97316; font-weight: bold; margin-left: 10px;">${summary.totalMonthOvertime}</span></p>
            </div>
          </div>
        </div>

        <div style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 15px; overflow: hidden; box-shadow: 0 5px 15px rgba(0,0,0,0.05); margin-bottom: 30px;">
          <div style="background: #0056b3; color: white; padding: 12px 25px; font-size: 1.3rem; font-weight: 600;">
            Attendance Analytics & Compliance Overview
          </div>
          <div style="padding: 20px; background: #ffffff; display: flex; justify-content: center;">
             <div id="chart-clone-target" style="width: 100%; max-width: 800px;"></div>
          </div>
        </div>
      `, analysisCardEl, true);


      // SECTION 1.5: PUNCH-IN RATIO GRAPH
      if (ratioChartEl) {
        await addSection(`
          <div style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 15px; overflow: hidden; box-shadow: 0 5px 15px rgba(0,0,0,0.05); margin-top: 20px;">
            <div style="background: #0056b3; color: white; padding: 18px 25px; font-size: 1.3rem; font-weight: 600;">
              Monthly Punch-In Time Ratio Analysis
            </div>
            <div style="padding: 25px; background: #ffffff;">
              <div id="ratio-chart-placeholder" style="width: 100%; height: 500px; display: flex; align-items: center; justify-content: center; background: #ffffff;">
                <div id="chart-clone-target"></div>
              </div>
              <div style="margin-top: 25px; padding: 15px; background: #fdfdfd; border-radius: 10px; border: 1px solid #f0f0f0;">
                <strong style="color: #666; font-size: 0.9rem;">Punch-In Trend Key:</strong>
                <div style="display: flex; gap: 20px; font-size: 0.85rem; margin-top: 10px;">
                  <div style="display: flex; align-items: center;"><div style="width: 12px; height: 12px; background: #28a745; border-radius: 50%; margin-right: 6px;"></div> Present</div>
                  <div style="display: flex; align-items: center;"><div style="width: 12px; height: 12px; background: #ffb300; border-radius: 50%; margin-right: 6px;"></div> Late</div>
                  <div style="display: flex; align-items: center;"><div style="width: 12px; height: 12px; background: #0d6efd; border-radius: 50%; margin-right: 6px;"></div> Miss Punch</div>
                  <div style="display: flex; align-items: center;"><div style="width: 12px; height: 12px; background: #dc3545; border-radius: 50%; margin-right: 6px;"></div> Absent</div>
                </div>
              </div>
            </div>
          </div>
        `, ratioChartEl);
      }


      // SECTION 2: LOGS (Compacted to fit more)
      await addSection(`
        <h2 style="color: #0056b3; border-left: 10px solid #0056b3; padding-left: 15px; font-size: 1.4rem; margin-bottom: 20px;">Detailed Attendance Log</h2>
        <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem; text-align: center;">
          <thead style="background-color: #0056b3; color: white;">
            <tr>
              <th style="padding: 10px; border: 1px solid #dee2e6;">Date</th>
              <th style="padding: 10px; border: 1px solid #dee2e6;">Day</th>
              <th style="padding: 10px; border: 1px solid #dee2e6;">Status</th>
              <th style="padding: 10px; border: 1px solid #dee2e6;">In</th>
              <th style="padding: 10px; border: 1px solid #dee2e6;">Out</th>
              <th style="padding: 10px; border: 1px solid #dee2e6;">Hours</th>
              <th style="padding: 10px; border: 1px solid #dee2e6;">Overtime</th>
            </tr>
          </thead>
          <tbody>
            ${summary.dailyReport.map(day => `
              <tr style="background-color: ${day.day === 'Sun' || day.status === 'W.Off' ? '#fcfcfc' : 'white'}; border-bottom: 1px solid #eee;">
                <td style="padding: 8px; border: 1px solid #dee2e6;">${day.date}</td>
                <td style="padding: 8px; border: 1px solid #dee2e6;">${day.day}</td>
                <td style="padding: 8px; border: 1px solid #dee2e6; color: ${day.status === 'Present' ? '#28a745' : day.status === 'Absent' ? '#dc3545' : day.status === 'Miss-Punch' ? '#0d6efd' : '#333'}; font-weight: bold;">${day.status}</td>
                <td style="padding: 8px; border: 1px solid #dee2e6;">${day.in}</td>
                <td style="padding: 8px; border: 1px solid #dee2e6;">${day.out}</td>
                <td style="padding: 8px; border: 1px solid #dee2e6;">${day.hours}</td>
                <td style="padding: 8px; border: 1px solid #dee2e6; color: #f97316; font-weight: ${day.overtime !== '-' ? 'bold' : 'normal'}">${day.overtime || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div style="margin-top: 50px; text-align: center; border-top: 1px solid #eee; padding-top: 15px; color: #666; font-size: 0.8rem;">
          <p><strong>Digi Sahyadri HRM - Performance Report</strong></p>
          <p>Emp: ${summary.employeeName} | ID: ${summary.employeeCode} | Date: ${moment().format('LLL')}</p>
        </div>
      `);

      pdf.save(`Attendance_Report_${summary.employeeName}_${monthName}_${year}.pdf`);

    } catch (error) {
      console.error('PDF Export Error:', error);
      alert('Failed to export PDF.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditRecord(record);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    fetchAttendance();
    setShowEditModal(false);
    setEditRecord(null);
  };

  const handleFestivalsChanged = async () => {
    const list = await loadFestivals();
    if (selectedEmployee) {
      fetchAttendance();
    } else {
      const days = getCalendarDays(month, year);
      setCalendarData(mapAttendanceToCalendar(days, [], list));
    }
  };

  return (
    <div className={`attendance-calendar-view ${isDashboard ? 'p-0' : ''}`}>
      {!hideEmployeeSelector && !hideMonthNavigator && (
        <Row className="mb-3 no-print align-items-center">
          <Col md={hideMonthNavigator ? 12 : 5}>
            {!hideEmployeeSelector && (
              <EmployeeSelector
                selectedEmployeeIds={selectedEmployee}
                onEmployeeChange={handleEmployeeChange}
                onEmployeeSelect={handleEmployeeSelect}
                isMultiSelect={false}
                disabled={isEmployee}
                month={month}
                year={year}
              />
            )}
          </Col>
          <Col md={hideEmployeeSelector ? 8 : 4}>
            {!hideMonthNavigator && (
              <MonthNavigator
                month={month}
                year={year}
                onMonthChange={handleMonthChange}
              />
            )}
          </Col>
          <Col md={hideEmployeeSelector ? 4 : 3} className="text-end d-flex justify-content-end align-items-center gap-2">
            {canManageFestivals && (
              <Button
                variant="outline-warning"
                size="sm"
                className="rounded-pill fw-bold shadow-sm d-inline-flex align-items-center"
                style={{
                  background: 'linear-gradient(90deg, rgba(255,56,56,0.1), rgba(255,159,26,0.1), rgba(50,255,126,0.1), rgba(125,95,255,0.1))',
                  borderColor: '#ff9f1a',
                  color: '#b83280'
                }}
                onClick={() => {
                  setFestivalModalDate(moment().format('YYYY-MM-DD'));
                  setShowFestivalModal(true);
                }}
              >
                <span className="me-1">🎉</span> Add Festival
              </Button>
            )}
            <Button variant="outline-primary" size="sm" onClick={handleExportPDF} disabled={loading} className="rounded-pill">
              {loading ? <Spinner as="span" animation="border" size="sm" /> : <><FaFilePdf className="me-1" /> PDF Export</>}
            </Button>
          </Col>
        </Row>
      )}

      {/* If simple navigator is requested but main row is hidden */}
      {hideEmployeeSelector && !hideMonthNavigator && (
        <div className="mb-3 d-flex justify-content-between align-items-center no-print">
           <MonthNavigator
                month={month}
                year={year}
                onMonthChange={handleMonthChange}
              />
           <div className="d-flex gap-2">
              {canManageFestivals && (
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="rounded-pill fw-bold"
                  style={{
                    background: 'linear-gradient(90deg, rgba(255,56,56,0.1), rgba(255,159,26,0.1), rgba(50,255,126,0.1), rgba(125,95,255,0.1))',
                    borderColor: '#ff9f1a',
                    color: '#b83280'
                  }}
                  onClick={() => {
                    setFestivalModalDate(moment().format('YYYY-MM-DD'));
                    setShowFestivalModal(true);
                  }}
                >
                  <span className="me-1">🎉</span> Add Festival
                </Button>
              )}
              <Button variant="outline-primary" size="sm" onClick={handleExportPDF} disabled={loading} className="rounded-pill">
                 {loading ? <Spinner as="span" animation="border" size="sm" /> : <><FaFilePdf className="me-1" /> PDF Export</>}
               </Button>
           </div>
        </div>
      )}

      {loading && !summary ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Calculating Attendance Intelligence...</p>
        </div>
      ) : (
        <>
          {showDashboardStats && summary && (
            <Row className="mb-4 g-3 no-print">
              <Col md={3} xs={6}>
                <div className="p-3 rounded-4 bg-light border-start border-5 border-success shadow-sm">
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2 bg-success bg-opacity-10 rounded-3 text-success">
                      <FaCheckCircle size={20} />
                    </div>
                    <div>
                      <h4 className="fw-bold mb-0">{summary.presentDays}</h4>
                      <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Present</small>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={3} xs={6}>
                <div className="p-3 rounded-4 bg-light border-start border-5 border-danger shadow-sm">
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2 bg-danger bg-opacity-10 rounded-3 text-danger">
                      <FaExclamationTriangle size={20} />
                    </div>
                    <div>
                      <h4 className="fw-bold mb-0">{summary.absentDays}</h4>
                      <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Absent</small>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={2} xs={6}>
                <div className="p-3 rounded-4 bg-light border-start border-5 border-info shadow-sm">
                  <div>
                    <h5 className="fw-bold mb-0">{summary.totalMissPunch}</h5>
                    <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Miss Punch</small>
                  </div>
                </div>
              </Col>
              <Col md={2} xs={6}>
                <div className="p-3 rounded-4 bg-light border-start border-5 border-warning shadow-sm">
                  <div>
                    <h5 className="fw-bold mb-0">{summary.lateMarks}</h5>
                    <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Late Marks</small>
                  </div>
                </div>
              </Col>
              <Col md={2} xs={12}>
                <div className="p-3 rounded-4 bg-primary text-white shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                  <h5 className="fw-bold mb-0">{summary.presentPercent}%</h5>
                  <small className="text-uppercase fw-bold" style={{ fontSize: '10px', opacity: 0.8 }}>Efficiency</small>
                </div>
              </Col>
            </Row>
          )}

          <Row>
            <Col lg={isDashboard ? 12 : 9}>
              <CalendarGrid
                month={month}
                year={year}
                attendanceData={calendarData}
                onDayClick={handleDayClick}
              />
              <div className="mt-3">
                <CalendarLegend />
              </div>
            </Col>
            {!isDashboard && !hideStats && (
              <Col lg={3}>
                {summary && selectedEmployee && (
                  <AttendanceSummaryCard summary={summary} />
                )}
              </Col>
            )}
          </Row>

          {/* Punch-In Analysis Graph - Hidden in dashboard for cleaner look */}
          {!isDashboard && summary && summary.dailyReport && (
            <Row className="mt-2 mx-0">
              <Col xs={12} className="px-0">
                <AttendanceGraph
                  dailyReport={summary.dailyReport}
                  month={month}
                  year={year}
                />
              </Col>
            </Row>
          )}
        </>
      )}

      {DetailModal && (
        <DetailModal
          show={showDetailModal}
          date={selectedDay?.date}
          attendance={selectedDay?.attendance}
          festival={selectedDay?.festival || selectedDay?.attendance?.festival}
          onClose={() => setShowDetailModal(false)}
          onEdit={isPrivilegedUser ? handleEdit : null}
          onAddFestival={(d) => {
            setFestivalModalDate(moment(d).format('YYYY-MM-DD'));
            setShowDetailModal(false);
            setShowFestivalModal(true);
          }}
          onDeleteFestival={async (fest) => {
            await deleteFestival(fest.id, fest.date);
            setShowDetailModal(false);
            handleFestivalsChanged();
          }}
          canManageFestivals={canManageFestivals}
          isPrivilegedUser={isPrivilegedUser}
        />
      )}

      {FestivalModal && (
        <FestivalModal
          show={showFestivalModal}
          onClose={() => setShowFestivalModal(false)}
          initialDate={festivalModalDate}
          festivals={festivals}
          canManageFestivals={canManageFestivals}
          onFestivalsChanged={handleFestivalsChanged}
        />
      )}

      <EditAttendanceModal
        show={showEditModal}
        record={editRecord}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default AttendanceCalendarView;
